/**
 * ToolCallCard — collapsible card showing a single tool invocation: name,
 * input args, output (or error), and timing.
 *
 * Specialised output renderers:
 *   - artifact_type === "plotly_chart" → inline PlotlyChartRender.
 *   - artifact_type === "data_value"   → inline DataValueChip ("verified
 *     data" badge for single-value lookups via get_cell_metric).
 *   - artifact_type === "nav_link"     → primary-action button linking
 *     to /explorer, /compare, or a cell detail page (Ion nav tools).
 *   - everything else → JSON-pre fallback.
 *
 * The check looks in both `output.result.artifact_type` (executor's
 * {result:{...}} envelope) and `output.artifact_type` (flat shape used
 * by mocks) — same unwrap-once logic as chatStore.endToolCall.
 */

import { useState } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { t } from '../../i18n/index.js'
import { AskUserDialog } from './AskUserDialog.jsx'
import { WebConsentCard } from './WebConsentCard.jsx'
import { DataValueChip } from './DataValueChip.jsx'
import { PlotlyChartRender } from './PlotlyChartRender.jsx'
import { applyNavLink } from './navLinkUtils.js'

function formatDuration(start, end) {
  if (!start || !end) return null
  const ms = end - start
  if (ms < 1000) return `${ms} ms`
  return `${(ms / 1000).toFixed(2)} s`
}

function formatJson(value) {
  if (value == null) return ''
  try {
    return JSON.stringify(value, null, 2)
  } catch (e) {
    return String(value)
  }
}

/**
 * @param {object} props
 * @param {object} props.toolCall — { id, name, input, output, status, startedAt, endedAt, isError }
 * @param {boolean} [props.defaultOpen=false]
 * @param {import('preact').ComponentChildren} [props.badge] — shown after the name (Ion Next provenance)
 */
export function ToolCallCard({ toolCall, defaultOpen = false, badge = null }) {
  const [open, setOpen] = useState(defaultOpen)
  const [, setLocation] = useLocation()

  if (!toolCall) return null

  const { name, input, output, status, startedAt, endedAt, isError } = toolCall
  const duration = formatDuration(startedAt, endedAt)
  const stateClass = isError
    ? 'ion-tool-card--error'
    : status === 'running'
      ? 'ion-tool-card--running'
      : 'ion-tool-card--done'

  return (
    <div class={`ion-tool-card ${stateClass}`}>
      <button
        type="button"
        class="ion-tool-card__head"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span class="ion-tool-card__chevron" aria-hidden="true">
          {open ? '▾' : '▸'}
        </span>
        <span class="ion-tool-card__name">{name}</span>
        {badge}
        {status === 'running' && (
          <span class="ion-tool-card__status ion-tool-card__status--running">
            {t('ion.tool.running')}
          </span>
        )}
        {isError && (
          <span class="ion-tool-card__status ion-tool-card__status--error">
            {t('ion.tool.error')}
          </span>
        )}
        {duration && (
          <span class="ion-tool-card__duration">{duration}</span>
        )}
      </button>
      {open && (
        <div class="ion-tool-card__body">
          {input && Object.keys(input).length > 0 && (
            <section class="ion-tool-card__section">
              <h4 class="ion-tool-card__section-title">{t('ion.tool.section.input')}</h4>
              <pre class="ion-tool-card__code">{formatJson(input)}</pre>
            </section>
          )}
          {output != null && (() => {
            // Detect a plot artifact in either envelope shape and route
            // to the dedicated renderer. Falls back to JSON-pre for
            // every other tool output (text returns, table data, etc.).
            const artifact =
              !isError && output.artifact_type
                ? output
                : !isError && output.result && output.result.artifact_type
                  ? output.result
                  : null
            if (artifact && artifact.artifact_type === 'plotly_chart') {
              return (
                <section class="ion-tool-card__section">
                  <h4 class="ion-tool-card__section-title">
                    {t('ion.tool.section.output')}
                  </h4>
                  <PlotlyChartRender
                    figure={artifact.plotly}
                    alt={artifact.alt}
                    caption={artifact.caption}
                    tool={artifact.tool || name}
                    skippedCells={artifact.skipped_cells}
                    isVerified={artifact.is_verified}
                    chain={artifact.chain}
                  />
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'data_value') {
              // Inline verified-data chip — what the user sees instead
              // of the AI restating the value in plain text.
              return (
                <section class="ion-tool-card__section">
                  <h4 class="ion-tool-card__section-title">
                    {t('ion.tool.section.output')}
                  </h4>
                  <DataValueChip
                    cell={artifact.cell}
                    metric={artifact.metric}
                    formatted={artifact.formatted}
                    basis={artifact.basis}
                    provenanceTrail={artifact.provenance_trail}
                  />
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'data_values') {
              // Multi-value bundle (get_cell_metrics) — list every value
              // as a chip so the admin sees exactly what the customer's
              // inline [[V:N]] citations can resolve to.
              return (
                <section class="ion-tool-card__section">
                  <h4 class="ion-tool-card__section-title">
                    {t('ion.tool.section.output')}
                  </h4>
                  <div class="ion-tool-card__data-values">
                    {(artifact.values || []).map((v) => (
                      <div
                        key={v.metric_name}
                        class="ion-tool-card__data-values-row"
                      >
                        <span class="ion-tool-card__data-values-label">
                          {v.label || v.metric_name}
                          {v.ref != null && (
                            <code class="ion-tool-card__data-values-ref">
                              {' '}V:{v.ref}
                            </code>
                          )}
                        </span>
                        {v.missing ? (
                          <span class="ion-tool-card__data-values-na">—</span>
                        ) : (
                          <DataValueChip
                            cell={artifact.cell}
                            metric={{
                              name: v.metric_name,
                              label: v.label,
                              unit: v.unit,
                            }}
                            formatted={v.formatted}
                          />
                        )}
                      </div>
                    ))}
                    {Array.isArray(artifact.skipped_metrics) &&
                      artifact.skipped_metrics.length > 0 && (
                        <p class="ion-tool-card__data-values-skipped">
                          Skipped: {artifact.skipped_metrics.join(', ')}
                        </p>
                      )}
                  </div>
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'nav_link') {
              // Primary-action button — opens the linked URL via the
              // SPA router so the page state survives the navigation.
              // External URLs (none today) would need <a target=> instead.
              return (
                <section class="ion-tool-card__section">
                  <h4 class="ion-tool-card__section-title">
                    {t('ion.tool.section.output')}
                  </h4>
                  <div class="ion-nav-link">
                    <button
                      type="button"
                      class="ion-nav-link__button"
                      onClick={() => applyNavLink(artifact.url, setLocation)}
                    >
                      {artifact.label || 'Open'}
                    </button>
                    {artifact.filter_summary && (
                      <p class="ion-nav-link__summary">
                        {artifact.filter_summary}
                      </p>
                    )}
                  </div>
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'ask_user') {
              // Render the dialog so admins can preview the same UI a
              // customer would see — clicking an option still submits
              // a follow-up message in the admin chat.
              return (
                <section class="ion-tool-card__section">
                  <h4 class="ion-tool-card__section-title">
                    {t('ion.tool.section.output')}
                  </h4>
                  <AskUserDialog toolCallId={toolCall.id} payload={artifact} />
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'web_consent_request') {
              return (
                <section class="ion-tool-card__section">
                  <WebConsentCard payload={artifact} />
                </section>
              )
            }
            if (artifact && artifact.artifact_type === 'web_results') {
              // Raw web results are not surfaced (admin feedback #41) — the
              // model's prose summary with inline source links is the only
              // user-facing view.
              return null
            }
            return (
              <section class="ion-tool-card__section">
                <h4 class="ion-tool-card__section-title">
                  {isError ? t('ion.tool.section.error') : t('ion.tool.section.output')}
                </h4>
                <pre class="ion-tool-card__code">{formatJson(output)}</pre>
              </section>
            )
          })()}
          {status === 'running' && (
            <p class="ion-tool-card__pending">{t('ion.tool.waiting')}</p>
          )}
        </div>
      )}
    </div>
  )
}
